import { Directive, Input, OnInit } from '@angular/core'
import { ControlValueAccessor, UntypedFormArray, UntypedFormBuilder } from '@angular/forms'
import { Tag, TagList } from 'src/utils/types'

@Directive()
export class TagControlComponentBase implements OnInit, ControlValueAccessor {
  private tagChangeFunc: any = null

  // This control works via an embedded form within the control.
  // As the fields in the embedded form change, the overall value
  // is published to the form that contains this control.
  tagForm = this.formBuilder.group({
    tagFormArray: this.formBuilder.array([]),
  })

  // Monotonically increasing ID that uniquely identifies each row.  This is used
  // for Angular's *ngFor directive to be able to identify DOM elements that do/don't
  // need re-creation when re-rendering the DOM.
  private rowId = 0

  constructor(private formBuilder: UntypedFormBuilder) {}

  private buildTagEntry(tagName: string, tagValue: string) {
    /**
     * Helper that ensures we get the unique rowId on each row.
     */
    return { tagName: tagName, tagValue: tagValue, rowId: this.rowId++ }
  }

  private emptyTagLine() {
    /**
     * Helper that creates the empty tag line row in the UI.
     */
    return this.formBuilder.group(this.buildTagEntry('', ''))
  }

  ngOnInit() {
    this.tagForm.valueChanges.subscribe((val) => {
      // publishes changes from the embedded form to the form that embeds this control
      if (this.tagChangeFunc) {
        this.tagChangeFunc(this.tags)
      }
      // make sure there's always an empty line in the tag list.
      this.ensureEmptyFormLine()
    })
  }

  get tags() {
    /**
     * Returns the list of tags, filtering out any values that don't have a tagName.
     * Empty tagValue is converted to a null.
     */
    let tagFormArray = this.tagForm.value.tagFormArray as Array<{ tagName: string; tagValue: string }>
    let sanitizedArray = tagFormArray
      .filter(({ tagName: tagName, tagValue: tagValue }) => {
        return tagName.length > 0
      })
      .map(({ tagName: tagName, tagValue: tagValue }) => {
        return { tagName: tagName, tagValue: tagValue?.length > 0 ? tagValue : null }
      })
    return sanitizedArray
  }

  private initializeForm(selectionTags: TagList) {
    let tagArray = selectionTags.map((tag: Tag) => {
      return this.formBuilder.group(this.buildTagEntry(tag.tagName, tag.tagValue))
    })
    this.tagForm.setControl('tagFormArray', this.formBuilder.array(tagArray))
  }

  private ensureEmptyFormLine() {
    let that = this
    function addEmptyTagLine() {
      ;(that.tagForm.get('tagFormArray') as UntypedFormArray).push(that.emptyTagLine())
    }

    let tagArray = this.tagForm.value.tagFormArray
    if (tagArray.length == 0) {
      addEmptyTagLine()
    } else {
      let lastLine = tagArray[tagArray.length - 1]
      let emptyLine = this.emptyTagLine().value
      if (lastLine.tagName !== emptyLine.tagName || lastLine.tagValue !== emptyLine.tagValue) {
        addEmptyTagLine()
      }
    }
  }

  removeTagAt(i: number) {
    let tagFormArray = this.tagForm.get('tagFormArray') as UntypedFormArray
    tagFormArray.removeAt(i)
  }

  tagTrackBy(i, obj) {
    return obj.rowId
  }

  tagNameTrackBy(i, obj) {
    return obj.key
  }

  tagValueTrackBy(i, obj) {
    return obj
  }

  writeValue(selectionTags: Array<{ tagName: string; tagValue: string }>): void {
    /**
     * Accept initial value from the HTML that embeds this control
     */
    if (selectionTags !== undefined) {
      this.initializeForm(selectionTags)
    }
  }

  registerOnChange(fn: any): void {
    /**
     * Capture the function to call when the value changes
     */
    this.tagChangeFunc = fn
  }

  registerOnTouched(fn: any): void {
    // nothing to do here - we don't handle touch events
  }
}
